// Export my data (FR-30): after step-up "export" the server builds a JSON file
// with everything it holds about the person and keeps it for 24 hours. The
// file is fetched with the bearer token and saved through the download helper;
// its contents are never read, logged or kept by the page.
import { Button, Group, Paper, Stack, Text, Title } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type Schemas } from "../../api/client";
import { errorMessage } from "../../shared/session/errors";
import { useStepUp } from "../../shared/session/stepup";
import { saveBlob } from "../../shared/ui/download";
import { useFormat } from "../../shared/ui/format";
import { FormError, Notice } from "../../shared/ui/Page";

type ExportLink = Schemas["ExportLink"];

/** fileName reads the name from Content-Disposition, falling back to one built from the export id. */
export function fileName(disposition: string | null, id: string): string {
  const m = disposition?.match(/filename="?([^";]+)"?/i);
  const name = m?.[1]?.trim();
  return name && /^[\w.-]+$/.test(name) ? name : `my-data-${id.slice(0, 8)}.json`;
}

export function ExportSection() {
  const { t } = useTranslation();
  const f = useFormat();
  const stepUp = useStepUp();
  const [link, setLink] = useState<ExportLink | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const download = async (l: ExportLink) => {
    const { data, error, response } = await api.GET("/me/export/{exportId}", {
      params: { path: { exportId: l.id } },
      parseAs: "blob",
    });
    if (!data) {
      if (response.status === 404) setLink(null);
      return setError(errorMessage(error, t("error.generic")));
    }
    saveBlob(fileName(response.headers.get("Content-Disposition"), l.id), data);
  };

  const create = async () => {
    setError(null);
    const token = await stepUp("export");
    if (!token) return;
    setBusy(true);
    const { data, error } = await api.POST("/me/export", { params: { header: { "X-Step-Up-Token": token } } });
    if (!data) {
      setBusy(false);
      return setError(errorMessage(error, t("error.generic")));
    }
    setLink(data);
    await download(data);
    setBusy(false);
  };

  const again = async () => {
    if (!link) return;
    setError(null);
    setBusy(true);
    await download(link);
    setBusy(false);
  };

  return (
    <Paper
      withBorder
      p="md"
      radius="md"
      component="section"
      aria-labelledby="export-title"
      data-testid="export-section"
    >
      <Stack gap="sm">
        <Title order={2} size="h4" id="export-title">
          {t("export.title")}
        </Title>
        <Text size="sm">{t("export.intro")}</Text>
        <Text size="sm" c="dimmed">
          {t("export.expiry")}
        </Text>
        {link && <Notice>{t("export.ready", { time: f.dateTime(link.expires_at) })}</Notice>}
        <FormError message={error} />
        <Group gap="sm">
          <Button variant="light" loading={busy && !link} disabled={busy} onClick={() => void create()}>
            {t("export.button")}
          </Button>
          {link && (
            <Button variant="default" loading={busy} onClick={() => void again()}>
              {t("export.download_again")}
            </Button>
          )}
        </Group>
      </Stack>
    </Paper>
  );
}
